import React from 'react';
import { Box, Typography, Stack } from '@mui/joy';
import type { Task } from '../../types/tasks';
import { TaskCard } from './TaskCard';

interface TaskPreviewProps {
  tasks: Task[];
  isLoading: boolean;
  onTaskClick?: (task: Task) => void;
  onToggleComplete?: (taskId: number, completed: boolean) => void;
  maxTasks?: number;
  showViewAll?: boolean;
  onViewAll?: () => void;
}

export const TaskPreview: React.FC<TaskPreviewProps> = ({
  tasks,
  isLoading,
  onTaskClick,
  onToggleComplete,
  maxTasks = 5,
  showViewAll = true,
  onViewAll,
}) => {
  const visibleTasks = tasks.slice(0, maxTasks);
  const hasMoreTasks = tasks.length > maxTasks;

  if (isLoading) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography level="body-sm" color="neutral">
          Loading tasks...
        </Typography>
      </Box>
    );
  }

  if (tasks.length === 0) {
    return (
      <Box sx={{ py: 4, textAlign: 'center' }}>
        <Typography level="body-sm" color="neutral">
          No tasks available
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Stack spacing={1.5}>
        {visibleTasks.map((task) => (
          <TaskCard
            key={task.id}
            task={task}
            variant="compact"
            onClick={onTaskClick}
            onToggleComplete={onToggleComplete}
            showMenu={false}
          />
        ))}
      </Stack>
      
      {hasMoreTasks && showViewAll && (
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Typography
            level="body-sm"
            sx={{ 
              color: 'primary.main',
              cursor: 'pointer',
              textDecoration: 'underline',
              '&:hover': { color: 'var(--brand-text, #7013f5)' }
            }}
            onClick={onViewAll}
          >
            View all {tasks.length} tasks
          </Typography>
        </Box>
      )}
    </Box>
  );
};